// Dedicated cross-tab channel for the second-screen control console.
// Separate from the brainStore mirror channel. The console SENDS commands;
// the app tab RECEIVES them (see useControlBridge) and executes locally.

export const CONTROL_CHANNEL = 'atv-control';

export interface ControlCommand {
  kind: 'command';
  id: string;
  action: string;
  payload?: Record<string, unknown>;
  navigateTo?: string;
  ts: string; // caller-supplied ISO; never Date.now() inside domain code
}

/** One selectable thing in the app tab, as the console sees it. */
export interface RosterEntry {
  id: string;
  label: string;
}

/**
 * What the app tab currently HAS, so the console can aim a command at a specific
 * record instead of always hitting the first match. Sent with every status; kept
 * to ids + labels (never whole records) so the channel payload stays small.
 */
export interface ControlRoster {
  studios: RosterEntry[];
  artists: RosterEntry[];
  customers: RosterEntry[];
  bookings: RosterEntry[];
  deposits: RosterEntry[];
}

export const EMPTY_ROSTER: ControlRoster = {
  studios: [],
  artists: [],
  customers: [],
  bookings: [],
  deposits: [],
};

export interface ControlStatus {
  kind: 'status';
  clockIso: string;
  firedEventIds: string[];
  counts: { fired: number; remaining: number };
  executorId: string;
  ts: string;
  /** Optional so an older app tab (or a status built before rosters existed)
   *  still parses; the console falls back to EMPTY_ROSTER and aims at nothing. */
  roster?: ControlRoster;
}

export interface ControlHello {
  kind: 'hello';
  ts: string;
}

/**
 * What actually happened, sent straight back to the console. Without this a
 * refused command (unsigned waiver, self-approval block, nothing to act on) is
 * invisible to whoever pressed the button — the warning lands in the OTHER
 * window's console, which nobody is looking at during a demo.
 */
export interface ControlAck {
  kind: 'ack';
  commandId: string;
  ok: boolean;
  note?: string;
  ts: string;
}

export type ControlMessage = ControlCommand | ControlStatus | ControlHello | ControlAck;

// Monotonic id source. Not for security, just to key one-shot commands in the
// UI. Uses a counter so it does not depend on Date.now() (banned in domain
// code) and stays deterministic within a session.
let seq = 0;
function nextId(): string {
  seq += 1;
  return `cmd-${seq}`;
}

export function makeCommand(input: {
  action: string;
  payload?: Record<string, unknown>;
  navigateTo?: string;
  ts: string;
}): ControlCommand {
  return {
    kind: 'command',
    id: nextId(),
    action: input.action,
    payload: input.payload,
    navigateTo: input.navigateTo,
    ts: input.ts,
  };
}

export function makeStatus(input: Omit<ControlStatus, 'kind'>): ControlStatus {
  return { kind: 'status', ...input };
}

export function makeHello(ts: string): ControlHello {
  return { kind: 'hello', ts };
}

export function makeAck(input: Omit<ControlAck, 'kind'>): ControlAck {
  return { kind: 'ack', ...input };
}

export function parseMessage(raw: unknown): ControlMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (m.kind === 'command') {
    if (typeof m.action !== 'string' || typeof m.id !== 'string') return null;
    return m as unknown as ControlCommand;
  }
  if (m.kind === 'status') {
    if (typeof m.clockIso !== 'string' || typeof m.executorId !== 'string') return null;
    return m as unknown as ControlStatus;
  }
  if (m.kind === 'hello') {
    if (typeof m.ts !== 'string') return null;
    return m as unknown as ControlHello;
  }
  if (m.kind === 'ack') {
    if (typeof m.commandId !== 'string' || typeof m.ok !== 'boolean') return null;
    return m as unknown as ControlAck;
  }
  return null;
}

// --- Thin transport wrapper (kept separate from pure logic above) ----------
// Returns a no-op poster + unsubscribe when BroadcastChannel is unavailable
// (e.g. some test envs), so importing modules never crashes.

export interface ControlPort {
  post: (msg: ControlMessage) => void;
  subscribe: (handler: (msg: ControlMessage) => void) => () => void;
  close: () => void;
}

export function openControlPort(): ControlPort {
  if (typeof BroadcastChannel === 'undefined') {
    return { post: () => {}, subscribe: () => () => {}, close: () => {} };
  }
  const channel = new BroadcastChannel(CONTROL_CHANNEL);
  return {
    post: (msg) => channel.postMessage(msg),
    subscribe: (handler) => {
      const listener = (event: MessageEvent) => {
        const parsed = parseMessage(event.data);
        if (parsed) handler(parsed);
      };
      channel.addEventListener('message', listener);
      return () => channel.removeEventListener('message', listener);
    },
    close: () => channel.close(),
  };
}
